import { asArray } from "@foretagsplatsen/core-helpers/array";
import { isObjectLiteral } from "../../helpers/object-util.js";

const baseElementClass = "finsit-element";

export const defaultTag = "div";

/**
 * Check the arguments provided to the element function.
 * It makes sure that the options are separated from the children.
 *
 * @param {object} options - The options object
 * @param {element[]} children - The children to render
 * @return {{options:object, children:element[]}} The options and children
 */
export function checkArguments(options, ...children) {
	const usefulChildren = removeUselessChildren(children);
	if (isObjectLiteral(options)) {
		return {
			options,
			children: usefulChildren,
		};
	}

	return {
		options: {},
		children: [options, ...usefulChildren],
	};
}

/**
 * Make a renderer that render an element with the provided options and
 * children.
 *
 * @param {object} params - Object parameters
 * @param {object} params.options - The options object
 * @param {element[]} params.children - The children to render
 * @param {string} params.name - The name of the element
 * @param {function} params.fn - The function to render the element
 * @return {Renderable}
 */
export function elementRenderer({ options, children, name, fn }) {
	const {
		tag = defaultTag,
		options: rootOptions,
		children: rootChildren = children,
		ref: rootRef,
	} = fn(options, children);

	ensureDefaultOptions({ name, options, rootOptions });

	const root = (html) =>
		rootTagRenderer({
			as: options.as,
			html,
			tag,
			options: rootOptions,
			ref: options.ref,
			rootRef,
			children: asArray(rootChildren),
		});

	options.renderOn?.render(root);
	return root;
}

/**
 * Todo: Add JSDoc comment
 */
export function ensureDefaultOptions({ name, options, rootOptions }) {
	rootOptions.class = buildFullCssClass({ name, options, rootOptions });

	rootOptions["data-test"] = options.dataTest ?? name;

	rootOptions.title = options.title?.toString();
}

/**
 * Todo: Add JSDoc comment
 */
export function rootTagRenderer({
	html,
	tag,
	as,
	options,
	ref,
	rootRef,
	children = [],
}) {
	if (typeof tag === "function") {
		const node = tag({ html, options, children, defaultTag });
		if (!node) {
			throw new Error(
				"[Element] When providing a function for the `as` parameter, the function must returned the created tag brush",
			);
		}
		rootRef?.(node.element);
		ref?.(node.element);
		return;
	}

	if (options.disabled) {
		options.tabindex = "-1";
	}

	const node = html[as ?? tag](options, children);
	rootRef?.(node.element);
	ref?.(node.element);
}

function buildFullCssClass({ name, options, rootOptions }) {
	return [
		rootOptions.class,
		options.class,
		{ fullWidth: options.fullWidth },
		baseElementClass,
		`${baseElementClass}_${name}`,
	];
}

/**
 * Removes all the falsy values, and the booleans.
 *
 * If you want to render `true`, `false`, `null` or `undefined`
 * you can use `toString` to force a string representation.
 *
 * About `""`, we don't think there is a valid use case for rendering an empty
 * string.
 */
function removeUselessChildren(children) {
	return children.filter((each) => each && each !== true);
}
